/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Dialog from '@mui/material/Dialog'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import theme from 'src/theme'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAssignments,
  selectAssignments,
  selectAssignmentsStatus,
} from 'src/v3/profile/profile.slice'
import {
  AssignmentIcon,
  CloseIcon,
  CorrectIcon,
  IncorrectIcon,
  LaunchIcon,
  SearchIcon,
} from 'src/v3/core/icons'
import Subheader from 'src/v3/components/subheader'
import Loading from 'src/v3/components/loading'

function prepareAssignments<
  T extends {
    name: string
    dueDate: string
  },
>(assignments: T[], assignmentSearch: string, assignmentSort: string): T[] {
  return assignments
    .filter((a) =>
      a.name.toLowerCase().includes(assignmentSearch.toLowerCase()),
    )
    .sort((a, b) => {
      if (assignmentSort === 'name-az') {
        return a.name.localeCompare(b.name || '') || 0
      }
      if (assignmentSort === 'name-za') {
        return b.name.localeCompare(a.name || '') || 0
      }
      if (assignmentSort === 'date-a') {
        return dayjs(a.dueDate).diff(b.dueDate)
      }
      return dayjs(b.dueDate).diff(a.dueDate)
    })
}

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let assignments = useAppSelector(selectAssignments)
  let assignmentsStatus = useAppSelector(selectAssignmentsStatus)

  let [assignmentSearch, setAssignmentSearch] = useState<string>('')
  let [assignmentSort, setAssignmentSort] = useState<string>('date-a')
  let [assignmentTab, setAssignmentTab] = useState<string>('incomplete')
  let [courseInfo, setCourseInfo] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let filteredAssignments = [...assignments]

  if (assignmentTab === 'incomplete') {
    filteredAssignments = assignments.filter((a) => a.status === 'Incomplete')
  } else if (assignmentTab === 'completed') {
    filteredAssignments = assignments.filter((a) => a.status === 'Complete')
  }

  filteredAssignments = prepareAssignments(
    filteredAssignments,
    assignmentSearch,
    assignmentSort,
  )

  let assignmentDetail = assignments.find((a) => a.id === courseInfo)

  // Always fetch fresh data on page load
  useEffect(() => {
    dispatch(fetchAssignments())
  }, [])

  return (
    <>
      <Typography variant="h1">Assignments</Typography>
      <Typography
        color="text.secondary"
        sx={{
          textAlign: 'center',
          marginBottom: -1,
        }}
      >
        View your active and completed assignments
      </Typography>
      <Subheader title="Assignments" />
      <Box sx={{ display: 'flex', flexWrap: 'wrap-reverse', mt: '-6px' }}>
        <Tabs
          variant="scrollable"
          value={assignmentTab}
          onChange={(e, num) => setAssignmentTab(num)}
        >
          <Tab value="incomplete" label="Incomplete" />
          <Tab value="completed" label="Completed" />
          <Tab value="all" label="All" />
        </Tabs>
      </Box>
      <Divider sx={{ marginBottom: 1 }} />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          marginY: 2,
        }}
      >
        <TextField
          sx={{ width: 240 }}
          variant="outlined"
          size="small"
          label="Search Assignments"
          placeholder="Name"
          name="assignment-search"
          type="text"
          value={assignmentSearch}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setAssignmentSearch(event.target.value)
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl sx={{ width: 210 }}>
          <InputLabel size="small">Sort</InputLabel>
          <Select
            value={assignmentSort}
            label="Sort"
            variant="outlined"
            size="small"
            onChange={(event) => setAssignmentSort(event.target.value)}
          >
            <MenuItem value={'date-a'}>Due Date: Ascending</MenuItem>
            <MenuItem value={'date-d'}>Due Date: Descending</MenuItem>
            <MenuItem value={'name-az'}>Name: A-Z</MenuItem>
            <MenuItem value={'name-za'}>Name: Z-A</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 15,
          marginBottom: 15,
          overflowY: 'scroll',
          maxHeight: 500,
        }}
      >
        {filteredAssignments.length ? (
          filteredAssignments.map((assignment, i) => {
            let daysDiff = Math.abs(
              dayjs(assignment.dueDate).diff(dayjs(), 'days'),
            )
            return (
              <Card
                key={assignment.id + i}
                variant="outlined"
                sx={{
                  p: 2,
                  width: 340,
                  textAlign: 'center',
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 1,
                  '&:hover': {
                    cursor: 'pointer',
                    borderColor: theme.palette.primary.main,
                  },
                }}
                onClick={() => setCourseInfo(assignment.id)}
              >
                <Box>
                  <Typography variant="h6">
                    <AssignmentIcon color="primary" className="mb-1 mr-1" />
                    {assignment.name}
                  </Typography>
                </Box>
                <Box sx={{ mb: 0 }}>
                  {assignment.status === 'Incomplete' ? (
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <IncorrectIcon color="warning" />
                        <Typography
                          sx={{
                            color: theme.palette.warning.main,
                          }}
                        >
                          Incomplete
                        </Typography>
                      </Box>
                      <Typography>
                        Due Date:{' '}
                        {dayjs(assignment.dueDate).format('MM/DD/YYYY')}
                      </Typography>
                      {dayjs().isAfter(assignment.dueDate) ? (
                        <Typography color="text.secondary" variant="subtitle2">
                          {daysDiff} days past due
                        </Typography>
                      ) : (
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color:
                              daysDiff < 8
                                ? theme.palette.error.main
                                : 'text.secondary',
                          }}
                        >
                          {daysDiff === 0
                            ? 'Due Today'
                            : `${daysDiff} days until due`}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <CorrectIcon color="success" />
                        <Typography
                          sx={{
                            color: theme.palette.success.main,
                          }}
                        >
                          Completed
                        </Typography>
                      </Box>
                      <Typography>
                        Due Date:{' '}
                        {dayjs(assignment.dueDate).format('MM/DD/YYYY')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography>
                    {assignment.courses.length} Course
                    {assignment.courses.length === 1 ? '' : 's'}
                  </Typography>
                  {assignment.status === 'Incomplete' && (
                    <Typography color="text.secondary" variant="subtitle2">
                      {
                        assignment.courses.filter(
                          (c) => c.status === 'Complete',
                        ).length
                      }{' '}
                      Completed
                    </Typography>
                  )}
                </Box>
              </Card>
            )
          })
        ) : (
          <Typography color="text.secondary">
            There are no assignments matching these filters.
          </Typography>
        )}
      </div>
      <Dialog
        open={!!courseInfo}
        onClose={() => setCourseInfo('')}
        scroll="paper"
        fullWidth={true}
        maxWidth="xs"
      >
        <Card variant="outlined" className="p-3">
          <Box className="flex items-center px-2 mb-2 justify-content-between">
            <div className="flex items-center justify-center my-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <AssignmentIcon />
              </Avatar>
              <Typography className="pl-2">{assignmentDetail?.name}</Typography>
            </div>
            <Box sx={{ flexGrow: 1 }}></Box>
            <IconButton
              edge="end"
              aria-label="close"
              onClick={() => setCourseInfo('')}
              sx={{ marginTop: -3, marginLeft: 2 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              padding: 0,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
            }}
          >
            <div style={{ padding: 12 }}>
              <div className="font-weight-bold">Incomplete Courses</div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 5,
                  marginTop: 5,
                  overflow: 'scroll',
                  maxHeight: 200,
                }}
              >
                {assignmentDetail?.courses
                  ?.filter((c) => c.status === 'Incomplete')
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((course, i) => (
                    <Chip
                      key={course.id + i}
                      label={course.name}
                      icon={<LaunchIcon />}
                      onClick={() =>
                        navigate(`/self-paced/course/${course.id}`)
                      }
                    />
                  ))}
              </div>
            </div>
            <Divider />
            <div style={{ padding: 12 }}>
              <div className="font-weight-bold">Completed Courses</div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 5,
                  marginTop: 5,
                  overflow: 'scroll',
                  maxHeight: 200,
                }}
              >
                {assignmentDetail?.courses
                  ?.filter((c) => c.status === 'Complete')
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((course, i) => (
                    <Chip
                      key={course.id + i}
                      label={course.name}
                      icon={<LaunchIcon />}
                      onClick={() =>
                        navigate(`/self-paced/course/${course.id}`)
                      }
                    />
                  ))}
              </div>
            </div>
          </Box>
        </Card>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[assignmentsStatus]} />
    </>
  )
}
